/**
 * Claude Code tool page → Logins: the default login plus the extra logins
 * kept on this computer, so each project can run on its own account.
 */
import { useState } from "react";
import {
  ConfirmDialog,
  Icons,
  InputDialog,
  PocketRow,
  PocketSection,
} from "@/components/ui";
import {
  addCliLogin,
  isCliLoginSignedIn,
  removeCliLogin,
  renameCliLogin,
  type CliLogin,
  type CliLoginStatus,
} from "@/desktop/cli-logins";
import { useT } from "@/i18n";
import { settingsCliLoginsMessages } from "@/i18n/messages/settings-cli-logins";
import { useProjectStore } from "@/stores/project-store";
import {
  accountSummary,
  sameAccountAs,
  type LoginAccount,
} from "./cli-login-accounts";
import { CliLoginRow } from "./cli-login-row";
import { SettingsFieldError } from "./settings-text-field";
import { useCliLoginSignIn } from "./use-cli-login-sign-in";
import { useCliLogins } from "./use-cli-logins";

type Dialog =
  | { kind: "add" }
  | { kind: "rename"; login: CliLogin }
  | { kind: "remove"; login: CliLogin; projects: string[] };

export function CliLoginsSection({
  executablePath,
}: {
  executablePath: string;
}) {
  const t = useT(settingsCliLoginsMessages);
  const { logins, statuses, defaultStatus, readFailed, reload } = useCliLogins(
    "claude_cli",
    { withStatus: true },
  );
  const signIn = useCliLoginSignIn({ executablePath, onFinished: reload });
  const projects = useProjectStore((state) => state.projects);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startSignIn(login: CliLogin) {
    void signIn.start(login.id, t("signInWindowTitle", { name: login.name }));
  }

  async function add(name: string) {
    const login = await addCliLogin("claude_cli", name);
    setDialog(null);
    await reload();
    startSignIn(login);
  }

  async function rename(login: CliLogin, name: string) {
    await renameCliLogin(login.id, name);
    setDialog(null);
    await reload();
  }

  async function remove(login: CliLogin) {
    if (removing) return;
    setRemoving(true);
    setError(null);
    if (signIn.state?.loginId === login.id) signIn.stop();
    try {
      await removeCliLogin(login.id);
    } catch {
      setError(t("removeFailed"));
    } finally {
      setRemoving(false);
      setDialog(null);
    }
    await reload();
  }

  function askToRemove(login: CliLogin) {
    const using = projects
      .filter((project) => project.cli_logins?.claude_cli === login.id)
      .map((project) => project.name);
    if (using.length) setDialog({ kind: "remove", login, projects: using });
    else void remove(login);
  }

  const accounts: LoginAccount[] = [
    { id: null, name: t("defaultLogin"), status: defaultStatus },
    ...(logins ?? []).map((login) => ({
      id: login.id,
      name: login.name,
      status: statuses[login.id],
    })),
  ];
  const defaultAccount = accountSummary(defaultStatus);

  return (
    <PocketSection title={t("logins")} testId="cli-logins">
      <PocketRow
        testId="cli-login-default"
        leading={<Icons.User className="h-4 w-4 text-muted" />}
        title={t("defaultLogin")}
        secondLine={
          defaultAccount
            ? t("signedInAs", { account: defaultAccount })
            : t("defaultLoginLine")
        }
      />
      {logins?.map((login) => (
        <CliLoginRow
          key={login.id}
          login={login}
          signedIn={signedInState(statuses[login.id])}
          account={accountSummary(statuses[login.id])}
          sameAccountAs={sameAccountAs(
            { id: login.id, name: login.name, status: statuses[login.id] },
            accounts,
          )}
          run={signIn.state?.loginId === login.id ? signIn.state : null}
          signInBusy={signIn.active}
          removing={removing}
          onSignIn={() => startSignIn(login)}
          onStop={signIn.stop}
          onUseBrowser={signIn.openInBrowser}
          onRename={() => setDialog({ kind: "rename", login })}
          onRemove={() => askToRemove(login)}
        />
      ))}
      <PocketRow
        testId="cli-login-add"
        leading={<Icons.Plus className="h-4 w-4 text-muted" />}
        title={t("addLogin")}
        disabled={logins === null || signIn.active}
        onSelect={() => setDialog({ kind: "add" })}
      />
      <SettingsFieldError
        error={readFailed ? t("loginsReadFailed") : error}
        testId="cli-logins-error"
      />
      {dialog?.kind === "add" && (
        <InputDialog
          title={t("addLoginTitle")}
          inputLabel={t("loginName")}
          initialValue=""
          confirmLabel={t("add")}
          errorMessage={t("saveFailed")}
          maxLength={80}
          onCancel={() => setDialog(null)}
          onSubmit={add}
          data-testid="cli-login-add-dialog"
        />
      )}
      {dialog?.kind === "rename" && (
        <InputDialog
          title={t("renameLoginTitle")}
          inputLabel={t("loginName")}
          initialValue={dialog.login.name}
          errorMessage={t("saveFailed")}
          maxLength={80}
          onCancel={() => setDialog(null)}
          onSubmit={(name) => rename(dialog.login, name)}
          data-testid="cli-login-rename-dialog"
        />
      )}
      {dialog?.kind === "remove" && (
        <ConfirmDialog
          busy={removing}
          title={t("removeTitle", { name: dialog.login.name })}
          description={t("removeInUse", {
            projects: dialog.projects.join(t("listSeparator")),
          })}
          confirmLabel={t("remove")}
          onCancel={() => {
            if (!removing) setDialog(null);
          }}
          onConfirm={() => void remove(dialog.login)}
          data-testid="cli-login-remove-confirm"
        />
      )}
    </PocketSection>
  );
}

/** `undefined` while the check runs; `null` when it could not be read. */
function signedInState(
  status: CliLoginStatus | null | undefined,
): boolean | null | undefined {
  return status ? isCliLoginSignedIn(status) : status;
}
